import { useEffect } from "react";
import { Link } from "react-router-dom";
import { AudioWaveform, Loader2, Play, RotateCcw, ArrowUpRight, AlertTriangle, Network, ShieldAlert, ListChecks, CheckCircle2 } from "lucide-react";
import { useAria } from "../context/AriaContext";
import AriaFeedback from "./AriaFeedback";
import ReasoningPanel, { reasoningForHuddle } from "./ReasoningPanel";

const ICONS = {
  red_flag: AlertTriangle,
  topology_running: Loader2,
  topology_complete: CheckCircle2,
  pending_review: ListChecks,
  locked_account: ShieldAlert,
};

const TONE = {
  red_flag: "text-red-400",
  topology_running: "text-amber-300",
  topology_complete: "text-emerald-400",
  pending_review: "text-[var(--pa-gold)]",
  locked_account: "text-red-400",
};

export default function HuddleCard() {
  const aria = useAria();

  useEffect(() => {
    if (!aria.huddle && !aria.huddleLoading) aria.fetchHuddle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (aria.huddleLoading && !aria.huddle) {
    return (
      <div className="mt-8 bg-[var(--pa-surface)] border border-[var(--pa-border)] rounded-lg p-5 flex items-center gap-2 text-sm text-[var(--pa-text-muted)]" data-testid="huddle-loading">
        <Loader2 size={14} className="animate-spin text-[var(--pa-gold)]" /> ARIA is preparing your daily huddle…
      </div>
    );
  }

  if (!aria.huddle) return null;

  const { counts, items, spoken } = aria.huddle;
  const hasItems = items && items.length > 0;

  return (
    <section className="mt-8 bg-[var(--pa-surface)] border border-[var(--pa-gold)]/40 rounded-lg p-5 md:p-6" data-testid="huddle-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <AudioWaveform size={16} className="text-[var(--pa-gold)]" />
          <div className="kicker">ARIA · Daily Huddle</div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => aria.playHuddle({ force: false })}
            disabled={aria.speaking}
            data-testid="huddle-play"
            className="inline-flex items-center gap-1.5 text-xs text-[var(--pa-gold)] hover:underline disabled:opacity-50"
          >
            <Play size={12} /> {aria.speaking ? "Speaking…" : "Play briefing"}
          </button>
          <button
            onClick={() => aria.playHuddle({ force: true })}
            disabled={aria.huddleLoading || aria.speaking}
            data-testid="huddle-refresh"
            className="inline-flex items-center gap-1.5 text-xs text-[var(--pa-text-muted)] hover:text-[var(--pa-gold)] disabled:opacity-50"
            title="Re-fetch latest data and replay"
          >
            <RotateCcw size={12} /> Refresh
          </button>
        </div>
      </div>

      {spoken && (
        <p className="text-sm text-[var(--pa-text)] leading-relaxed mb-4" data-testid="huddle-spoken">
          {spoken}
        </p>
      )}

      {hasItems ? (
        <ul className="grid sm:grid-cols-2 gap-2" data-testid="huddle-items">
          {items.map((it, i) => {
            const Icon = ICONS[it.kind] || ArrowUpRight;
            return (
              <li key={i}>
                <Link
                  to={it.path}
                  data-testid={`huddle-item-${it.kind}`}
                  className="flex items-start gap-3 p-3 rounded-md bg-[var(--pa-bg-2)] border border-[var(--pa-border)] hover:border-[var(--pa-gold)]/50 transition"
                >
                  <Icon size={16} className={`mt-0.5 shrink-0 ${TONE[it.kind] || "text-[var(--pa-gold)]"} ${it.kind === "topology_running" ? "animate-spin" : ""}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[var(--pa-text)]">{it.label}</div>
                    <div className="text-[10px] text-[var(--pa-text-dim)] uppercase tracking-widest mt-0.5">
                      {it.kind.replace(/_/g, " ")}
                    </div>
                  </div>
                  <ArrowUpRight size={14} className="text-[var(--pa-text-dim)] shrink-0" />
                </Link>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="text-xs text-[var(--pa-text-dim)]" data-testid="huddle-empty">
          Nothing urgent on your board. Pick a clinical module to begin.
        </div>
      )}

      {/* Counts strip */}
      <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        <CountTile label="QA today" value={counts.quick_assessments_today} testid="huddle-qa-today" />
        <CountTile label="Red flags open" value={counts.red_flags_unacknowledged} testid="huddle-red-flags" tone={counts.red_flags_unacknowledged > 0 ? "text-red-400" : null} />
        <CountTile label="CCA last 7d" value={counts.case_analyses_last_7d} testid="huddle-cca-7d" />
        <CountTile label="Topology running" value={counts.topology_running} testid="huddle-topo-running" tone={counts.topology_running > 0 ? "text-amber-300" : null} />
      </div>

      {spoken && (
        <ReasoningPanel
          surface="huddle"
          reasoning={reasoningForHuddle(aria.huddle)}
        />
      )}

      {spoken && (
        <AriaFeedback
          surface="huddle"
          ariaResponse={spoken}
          originalQuery="Daily clinical huddle briefing"
        />
      )}
    </section>
  );
}

function CountTile({ label, value, testid, tone }) {
  return (
    <div className="bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded-md p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-[var(--pa-text-dim)]">{label}</div>
      <div className={`font-serif text-2xl mt-1 ${tone || "text-[var(--pa-text)]"}`}>{value}</div>
    </div>
  );
}
